import { Injectable } from '@angular/core';
import { Observable, fromEvent, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { IndActionMenuOption, IndActionMenuResult, IndActionMenuSection } from '@omni/components/shared/ind-action-menu/ind-action-menu';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { FOLLOW_UP_TYPE } from '@omni/classes/activity/follow-up-action.activity.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { ActivityCreationService, ProcedureLogOption, QuickCreateContext } from './activity-creation.service';
import { QuickActionMenuService } from './quick-action-menu.service';
import { DeviceService } from '../device/device.service';

/** Each surface pins independently: the bar is not the contact card is not the account card. */
export type QuickActionSource = 'topbar' | 'contact' | 'account';

const STORE_KEYS: Record<QuickActionSource, string> = {
  topbar: DB_KEY_PREFIXES.QUICK_CREATE_PINNED,
  contact: DB_KEY_PREFIXES.CONTACT_QUICK_ACTIONS,
  account: DB_KEY_PREFIXES.ACCOUNT_QUICK_ACTIONS,
};

// Read like stored pins, so a type that is not available is skipped rather than backfilled.
const DEFAULT_PINS: Partial<Record<QuickActionSource, string[]>> = {
  contact: ['quick-meeting', 'quick-message', 'request-hcp-consent', 'phone-call', 'consent'],
  account: ['quick-meeting', 'phone-call'],
};

/**
 * What the surface shows, and what is left over. `stored` of `null` means the rep has never
 * pinned, so declaration order stands in; an empty array is a rep who unpinned everything,
 * which is a legitimate state and must not be confused with the first.
 *
 * Ids are resolved against what is currently available, so a revoked feature action drops a
 * type from the strip without erasing its pin — it returns to its place if the action does.
 */
function splitByPins<T extends { id: string }>(available: T[], stored: string[] | null, cap: number)
  : { strip: T[]; overflow: T[] } {
  if (!stored) return { strip: available.slice(0, cap), overflow: available.slice(cap) };
  const strip = stored
    .map(id => available.find(item => item.id === id))
    .filter((item): item is T => !!item)
    .slice(0, cap);
  return { strip, overflow: available.filter(item => !strip.includes(item)) };
}

/**
 * Appended, so strip order is pin history (D3).
 *
 * Ids that do not resolve right now are kept, not dropped: availability can be per contact
 * (First Time Email hides on a consented one), so dropping would lose the pin for every contact.
 * They go after the new pin, so when one comes back past the cap it is that id, not the pin
 * just made, that `slice` pushes to the overflow.
 */
function withPin(stored: string[] | null, fallback: string[], id: string, available: Set<string>): string[] {
  const current = stored ?? fallback;
  if (current.includes(id)) return [...current];
  return [...current.filter(pinned => available.has(pinned)), id, ...current.filter(pinned => !available.has(pinned))];
}

/** Falling back to the implicit strip is what stops the first unpin dropping everything. */
function withoutPin(stored: string[] | null, fallback: string[], id: string): string[] {
  return (stored ?? fallback).filter(pinned => pinned !== id);
}

/** An activity a rep can start from a card, and what it takes to start it. */
export interface QuickActionType {
  id: string;
  labelKey: string;
  icon: string;
  isEnabled: () => boolean;
  /** isEnabled minus its per-record checks, so default pins stay put from one record to the next. */
  isAllowedForUser?: () => boolean;
  /** Offered but inert: the translation key saying why, or null while it can run. */
  disabledReason?: () => string | null;
  /** Present means the menu row expands in place instead of selecting (design C3). */
  menuOptions?: () => IndActionMenuOption[];
  /** Runs the type on a choice made in that sub-list, so it is not asked for twice. */
  runWithOptionId?: (optionId?: string) => Promise<void>;
  dictateWithOptionId?: (optionId?: string) => Promise<void>;
  run: (event?: Event) => void | Promise<unknown>;
}

/**
 * What the strip row fits, widest first. The queries are the app's own breakpoints
 * (ind-split-pane's md and lg), so the cap turns over where the layout does.
 */
const QUICK_ACTION_CAPS: { query: string, cap: number }[] = [
  { query: '(min-width: 992px)', cap: 6 },
  { query: '(min-width: 768px)', cap: 5 },
];
const QUICK_ACTION_CAP_PHONE = 4;
const QUICK_ACTION_CAP_TABLET = 5;

/**
 * The strip a glance card renders: which types are pinned to it, what is left behind the
 * overflow, and the menus both open. It owns no activity — the surface passes its own list in,
 * which is why the contact card, the account card and their timeline pages all share this.
 */
@Injectable({ providedIn: 'root' })
export class QuickActionStripService {

  private readonly pins = new Map<QuickActionSource, string[] | null>();
  private readonly loads = new Map<QuickActionSource, Promise<void>>();
  /** Surfaces whose stored list could not be read, and so must not be written over. */
  private readonly unread = new Set<QuickActionSource>();

  constructor(
    private readonly disk: DiskService,
    private readonly translate: TranslateService,
    private readonly globalUtility: GlobalUtilityService,
    private readonly menu: QuickActionMenuService,
    private readonly activityCreationService: ActivityCreationService,
    private readonly authService: AuthenticationService,
    private readonly notificationService: NotificationService,
    private readonly device: DeviceService,
  ) { }

  /** The Procedure Log tile both cards offer, created against the card's own customer. */
  public procedureLogType(context: () => QuickCreateContext): QuickActionType {
    const type: QuickActionType = {
      id: 'procedure-log', labelKey: 'NEW_ACTIVITY_PROCEDURE_LOG', icon: 'order_open.svg',
      isEnabled: () => this.activityCreationService.procedureLogCategories().length > 0,
      run: event => this.chooseOptionAndRun(event, [type], type.id),
      menuOptions: () => this.activityCreationService.procedureLogCategories()
        .map(category => ({ id: category.value, label: category.label })),
      runWithOptionId: async id => {
        await this.activityCreationService.createProcedureLogOf(id as ProcedureLogOption, context());
      },
    };
    return type;
  }

  /** The Follow-up tile both cards offer; the source decides which categories it lists. */
  public followUpType(source: QuickActionSource, context: () => QuickCreateContext): QuickActionType {
    const type: QuickActionType = {
      id: 'follow-up-action', labelKey: 'FOLLOW_UP_ACTION', icon: 'follow-up_32x32.svg',
      isEnabled: () => this.authService.hasFeatureAction(FeatureActionsMap.FOLLOW_UP_ACTION_ACTIVITY),
      run: event => this.chooseOptionAndRun(event, [type], type.id),
      menuOptions: () => this.activityCreationService.followUpCategories(source)
        .map(category => ({ id: category.value, label: category.label })),
      runWithOptionId: async id => {
        await this.activityCreationService.create('followUpAction', { followUpCategory: id as FOLLOW_UP_TYPE, context: context() });
      },
    };
    return type;
  }

  /**
   * The surface's pins, read once and held: a rebuilt card must not add another disk hit
   * before first paint. Callers do not await it — the strip renders in declaration order and
   * corrects itself when the read returns.
   */
  public loadPins(source: QuickActionSource): Promise<void> {
    const inFlight = this.loads.get(source);
    if (inFlight) return inFlight;
    const load = this.readPins(source);
    this.loads.set(source, load);
    return load;
  }

  private async readPins(source: QuickActionSource): Promise<void> {
    try {
      const doc = await this.disk.retrieve(STORE_KEYS[source], true);
      this.unread.delete(source);
      if (Array.isArray(doc?.raw)) this.pins.set(source, doc.raw);
    } catch (error) {
      // A failed read is not an empty one: declaration order is shown, but the surface is
      // marked unread so a later pin cannot save that order over the rep's real list.
      this.unread.add(source);
      console.error('Quick actions: could not read pins', error);
    }
  }

  /** Fire and forget: a pin that fails to store has still been applied to the surface. */
  private savePins(source: QuickActionSource, ids: string[]): void {
    if (this.unread.has(source)) {
      console.error('Quick actions: not saving pins over a list that could not be read');
      // Drop the cached load so the next read retries and the pin can be made again.
      this.loads.delete(source);
      return;
    }
    this.pins.set(source, ids);
    this.disk.updateOrInsert(STORE_KEYS[source], doc => ({ ...doc, raw: ids }))
      .catch(error => console.error('Quick actions: could not persist pins', error));
  }

  /**
   * Read per call, not cached: rotating or resizing crosses these boundaries at runtime. D10
   * keeps one stored list for every form factor, so a pin past the current cap is hidden
   * rather than dropped, and comes back on the screen that has room for it.
   */
  public cap(): number {
    // An iPad's cap follows the device, not its width, which changes with rotation and model.
    if (this.device.deviceFlags.iPad) return QUICK_ACTION_CAP_TABLET;
    return QUICK_ACTION_CAPS.find(tier => window.matchMedia(tier.query).matches)?.cap
      ?? QUICK_ACTION_CAP_PHONE;
  }

  /** Crossing a breakpoint changes the cap, so the host has to rebuild its strip. */
  public capChanges(): Observable<MediaQueryListEvent> {
    return merge(...QUICK_ACTION_CAPS.map(tier =>
      fromEvent<MediaQueryListEvent>(window.matchMedia(tier.query), 'change')));
  }

  /**
   * `Utility.globalCustomerText` is a translation key, not the word: passing it straight
   * through rendered "CUSTOMER Inquiry". GlobalUtilityService is what resolves it.
   */
  public label(type: QuickActionType): string {
    return this.translate.instant(type.labelKey, { globalCustomerText: this.globalUtility.globalCustomerText });
  }

  /** What the strip shows and what the overflow holds. With nothing pinned, the surface's defaults. */
  public split(types: QuickActionType[], source: QuickActionSource)
    : { strip: QuickActionType[]; overflow: QuickActionType[] } {
    const enabled = types.filter(type => type.isEnabled());
    // Defaults until the read returns; the stored pins then replace them.
    return splitByPins(enabled, this.pins.get(source) ?? this.defaultPins(types, source), this.cap());
  }

  /** Sized to the widest cap, so a first unpin on a phone does not drop what a wider screen shows. */
  private defaultPins(types: QuickActionType[], source: QuickActionSource): string[] | null {
    const defaults = DEFAULT_PINS[source];
    if (!defaults) return null;
    const allowedIds = types.filter(type => (type.isAllowedForUser ?? type.isEnabled)()).map(type => type.id);
    const allowedDefaults = defaults.filter(id => allowedIds.includes(id)).length;
    const fill = allowedIds.filter(id => !defaults.includes(id)).slice(0, QUICK_ACTION_CAPS[0].cap - allowedDefaults);
    return [...defaults, ...fill];
  }

  /**
   * The overflow menu. Pinning manages the strip rather than choosing from it, so the menu
   * stays open and redraws with the type moved between sections; `onPinned` is what lets the
   * card behind it follow.
   */
  public async openOverflow(event: Event, types: QuickActionType[], source: QuickActionSource,
    onPinned: () => void): Promise<void> {
    const data = await this.menu.present(event, {
      sections: this.sections(types, source),
      onPinToggle: (result: IndActionMenuResult) => {
        this.togglePin(types, source, result.action === 'pin' ? 'pin' : 'unpin', result.itemId);
        onPinned();
        return this.sections(types, source);
      },
    });
    if (!data || (data.action !== 'select' && data.action !== 'dictate')) return;

    const type = types.find(candidate => candidate.id === data.itemId);
    if (!type) return;
    if (data.action === 'dictate') {
      await type.dictateWithOptionId?.(data.optionId);
      return;
    }
    // A sub-list row carries the choice, so the type must not ask for it a second time. With no
    // options (Meeting, no formats) the row is the choice; run() would reopen the heading-only dropdown.
    if (type.runWithOptionId && (data.optionId || !type.menuOptions?.().length)) {
      await type.runWithOptionId(data.optionId);
      return;
    }
    await type.run(event);
  }

  /**
   * Asks only when there is a choice and a tile to anchor to; otherwise the first option runs.
   * The dropdown reuses the type's own menuOptions, so a configured note shows as in the overflow.
   */
  public async chooseOptionAndRun(event: Event | undefined, types: QuickActionType[], typeId: string): Promise<void> {
    const type = types.find(candidate => candidate.id === typeId);
    if (!type?.menuOptions || !type.runWithOptionId) return;
    const options = type.menuOptions();
    const dictatable = !!type.dictateWithOptionId && this.activityCreationService.canDictateMeeting();
    const picked = (options.length <= 1 && !dictatable) || !event
      ? (options[0] ? { optionId: options[0].id, dictate: false } : undefined)
      : await this.menu.pickOption(event, type.id, this.label(type), options, dictatable);
    if (!picked) return;
    if (picked.dictate) await type.dictateWithOptionId?.(picked.optionId);
    else await type.runWithOptionId(picked.optionId);
  }

  /** What a glance card host binds: the strip's tiles, and whether anything is left for the overflow. */
  public layout(types: QuickActionType[], source: QuickActionSource): { tiles: any[]; hasOverflow: boolean } {
    const { strip, overflow } = this.split(types, source);
    const tiles = strip.map(type => ({
      id: type.id,
      name: this.label(type),
      imgSrc: `assets/imgs/${type.icon}`,
      isDisabled: !!type.disabledReason?.(),
    }));
    return { tiles, hasOverflow: overflow.length > 0 };
  }

  /** Runs the tapped tile's type; false means the id is one of the host's own tools. */
  public async runById(types: QuickActionType[], id: string, event?: Event): Promise<boolean> {
    const type = types.find(candidate => candidate.id === id);
    if (!type) return false;
    const disabledReason = type.disabledReason?.();
    if (disabledReason) {
      this.notificationService.notify(this.translate.instant(disabledReason), 'Quick Actions', 'top', ToastStyle.INFO);
      return true;
    }
    await type.run(event);
    return true;
  }

  private sections(types: QuickActionType[], source: QuickActionSource): IndActionMenuSection[] {
    const { strip, overflow } = this.split(types, source);
    const isFull = strip.length >= this.cap();
    const toItem = (type: QuickActionType, pinned: boolean) => {
      const disabledReason = type.disabledReason?.();
      return {
        id: type.id,
        label: this.label(type),
        icon: `assets/imgs/${type.icon}`,
        disabled: !!disabledReason,
        disabledReason: disabledReason ? this.translate.instant(disabledReason) : undefined,
        options: type.menuOptions?.(),
        dictatable: !!type.dictateWithOptionId && this.activityCreationService.canDictateMeeting(),
        pinned,
        pinnable: true,
        // Greyed rather than hidden: a control that disappears never teaches the rep it exists.
        pinDisabled: !pinned && isFull,
      };
    };

    const sections: IndActionMenuSection[] = [];
    if (overflow.length) {
      sections.push({
        title: this.translate.instant('MORE_ACTIVITY_TYPES'),
        warning: isFull ? this.translate.instant('UNPIN_TO_ADD_ANOTHER') : undefined,
        counter: isFull ? undefined : this.translate.instant('N_OF_M_PINNED', {
          count: strip.length, cap: this.cap(),
        }),
        items: overflow.map(type => toItem(type, false)),
      });
    }
    if (strip.length) {
      sections.push({
        title: this.translate.instant('PINNED_ACTIVITIES'),
        items: strip.map(type => toItem(type, true)),
      });
    }
    return sections;
  }

  private togglePin(types: QuickActionType[], source: QuickActionSource,
    action: 'pin' | 'unpin', id: string): void {
    const enabled = types.filter(type => type.isEnabled());
    const fallback = this.defaultPins(types, source) ?? this.split(types, source).strip.map(type => type.id);
    const stored = this.pins.get(source) ?? null;
    const next = action === 'pin'
      ? withPin(stored, fallback, id, new Set(enabled.map(type => type.id)))
      : withoutPin(stored, fallback, id);
    this.savePins(source, next);
  }
}
